import { Checkbox, Form, Input, Modal, Select, message } from "antd";
import { DATE_TIME_FORMAT } from "common/constants";
import { formatError } from "common/redux/middleware/queryErrorLogger";
import { capitalizeFirstLetter } from "common/utils";
import { AktDatePicker } from "components/aktDatePicker";
import { CONSENT_OPTIONS, CONSENT_FIELDS } from "components/consentTag";
import { useUserType } from "features/auth";
import { usePostDebtorCommunicationHistoryMutation } from "features/communicationsTable/agencyPortal/communicationsTableAPI";
import { usePostCreditorPortalDebtorCommunicationHistoryMutation } from "features/communicationsTable/creditorPortal/communicationsTableAPI";
import { useFetchDebtorContactsQuery } from "features/debtorSider/agencyPortal/debtorSiderAPI";
import { useFetchCreditorPortalDebtorContactsQuery } from "features/debtorSider/creditorPortal/debtorSiderAPI";
import { useFetchBackendConstantsQuery } from "features/home/agencyPortal/homeAPI";
import { useFetchCommunicationResultItemsQuery } from "features/communicationResultItems/communicationResultItemsAPI";
import { useFetchDebtorAccountsQuery } from "features/debtorAccountsTable/agencyPortal/debtorAccountsAPI";

import moment from "moment-timezone";
import { useMemo, useState } from "react";
import styled from "styled-components";
import { useFetchCreditorPortalDebtorAccountsQuery } from "features/debtorAccountsTable/creditorPortal/debtorAccountsAPI";

const StyledForm = styled(Form)`
  margin-top: 12px;
  max-width: 400px;
`;

const DEBTOR_CONTACT_VALUE = "debtor_contact";

function CommunicationModal({ title, open, onOk, onCancel, debtorId }) {
  const { isAgencyUserType, isCreditorUserType } = useUserType();
  const [componentSize, setComponentSize] = useState("default");
  const { data: agencyDebtorContacts } = useFetchDebtorContactsQuery(
    { debtorId },
    { skip: !isAgencyUserType },
  );
  const { data: creditorPortalDebtorContacts } = useFetchCreditorPortalDebtorContactsQuery(
    { debtorId },
    { skip: !isCreditorUserType },
  );
  const { data: agencyDebtorAccounts } = useFetchDebtorAccountsQuery(
    { debtorId },
    { skip: !isAgencyUserType },
  );
  const { data: creditorDebtorAccounts } = useFetchCreditorPortalDebtorAccountsQuery(
    { debtorId },
    {
      skip: !isCreditorUserType,
    },
  );

  const debtorContacts = isAgencyUserType ? agencyDebtorContacts : creditorPortalDebtorContacts;
  const debtorAccounts = isAgencyUserType ? agencyDebtorAccounts : creditorDebtorAccounts;

  const [addAgencyCommunication, { isLoading: isAgencyCommunicationLoading }] =
    usePostDebtorCommunicationHistoryMutation();
  const [addCreditorCommunication, { isLoading: isCreditorCommunicationLoading }] =
    usePostCreditorPortalDebtorCommunicationHistoryMutation();
  const [addCommunicationAction, isLoading] = isAgencyUserType
    ? [addAgencyCommunication, isAgencyCommunicationLoading]
    : [addCreditorCommunication, isCreditorCommunicationLoading];

  const { data: constants } = useFetchBackendConstantsQuery();

  // Fetch all enabled communication result items once, then filter by type
  const { data: allResultItems = [] } = useFetchCommunicationResultItemsQuery({
    isEnabled: true,
  });
  const callResultItems = allResultItems.filter((item) => item.resultType === "call");
  const textResultItems = allResultItems.filter((item) => item.resultType === "text");
  const mailResultItems = allResultItems.filter((item) => item.resultType === "mail");
  const communicationResultItems = allResultItems.filter(
    (item) => item.resultType === "communication",
  );

  const extraRecipientOptions = (constants?.debtorCommunicationContactTypes ?? [])
    .filter(({ value }) => value !== DEBTOR_CONTACT_VALUE)
    .map(({ value, display }) => ({
      value,
      label: display,
    }));

  const filteredDebtorContacts = useMemo(
    () => debtorContacts?.filter((contact) => !contact.isArchived),
    [debtorContacts],
  );

  const onFormLayoutChange = ({ size }) => {
    setComponentSize(size);
  };
  const [form] = Form.useForm();

  const onOkPressed = async () => {
    let values;
    try {
      // Get the values from the form, then pass them to the outer `onOk` callback
      values = await form.validateFields();
    } catch {
      return null;
    }
    const result = await addCommunicationAction({ communication: values, debtorId });
    if ("data" in result) {
      onOk();
      message.success("Communication log added successfully!");
      form.resetFields();
    }
    if ("error" in result) {
      form.setFields(formatError(result.error));
    }
  };

  return (
    <Modal
      confirmLoading={isLoading}
      maskClosable={false}
      title={title}
      open={open}
      onOk={onOkPressed}
      onCancel={() => {
        onCancel();
        form.resetFields();
      }}
      okButtonProps={{
        loading: isLoading,
      }}
    >
      <StyledForm
        form={form}
        layout="vertical"
        initialValues={{
          size: componentSize,
          communicationStartTime: moment().format(DATE_TIME_FORMAT),
          communicationOrigin: "outbound",
          recipient: filteredDebtorContacts?.[0]?.id,
        }}
        onValuesChange={onFormLayoutChange}
        // @ts-ignore
        size={componentSize}
        labelAlign="left"
      >
        <Form.Item
          label="Contact method"
          name="contactMethod"
          rules={[{ required: true, message: "Contact method is required" }]}
          initialValue="phone"
        >
          <Select
            options={constants?.contactMethods.map((method) => ({
              label: method.display,
              value: method.value,
            }))}
          />
        </Form.Item>
        <Form.Item label="Communication Origin" name="communicationOrigin">
          <Select
            options={constants?.communicationOrigin
              ?.filter((communicationOrigin) => Boolean(communicationOrigin.value))
              .map((communicationOrigin) => ({
                label: communicationOrigin.display,
                value: communicationOrigin.value,
              }))}
          />
        </Form.Item>
        <Form.Item name="accountIds" label="Affected Accounts">
          <Select
            popupMatchSelectWidth={false}
            mode="multiple"
            allowClear
            options={debtorAccounts?.map((acct) => ({
              label: `Account #${acct.externalId}`,
              value: acct.id,
            }))}
            placeholder="Select Accounts..."
          />
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.contactMethod !== currentValues.contactMethod
          }
        >
          {({ getFieldValue }) => {
            const contactMethod = getFieldValue("contactMethod");
            if (
              contactMethod?.toLowerCase() === "phone" ||
              contactMethod?.toLowerCase() === "ai_dialer"
            ) {
              return (
                <Form.Item label="Call Results" name="callResults">
                  <Select
                    mode="multiple"
                    allowClear
                    options={callResultItems.map((item) => ({
                      label: item.name,
                      value: item.slug,
                    }))}
                  />
                </Form.Item>
              );
            }
            if (contactMethod?.toLowerCase() === "text") {
              return (
                <Form.Item label="Text Results" name="textResults">
                  <Select
                    mode="multiple"
                    allowClear
                    options={textResultItems.map((item) => ({
                      label: item.name,
                      value: item.slug,
                    }))}
                  />
                </Form.Item>
              );
            }
            if (contactMethod?.toLowerCase() === "mail") {
              return (
                <Form.Item label="Mail Results" name="mailResults">
                  <Select
                    mode="multiple"
                    allowClear
                    options={mailResultItems.map((item) => ({
                      label: item.name,
                      value: item.slug,
                    }))}
                  />
                </Form.Item>
              );
            }
          }}
        </Form.Item>
        <Form.Item label="Communication Results" name="communicationResults">
          <Select
            mode="multiple"
            allowClear
            options={communicationResultItems.map((item) => ({
              label: item.name,
              value: item.slug,
            }))}
          />
        </Form.Item>
        <Form.Item
          label="Recipient"
          name="recipient"
          rules={[{ required: true, message: "A recipient is required" }]}
        >
          <Select
            popupMatchSelectWidth={false}
            options={[
              ...(filteredDebtorContacts?.map((debtorContact) => ({
                value: debtorContact.id,
                label: [debtorContact.firstName, debtorContact.middleName, debtorContact.lastName]
                  .filter((name) => !!name)
                  .join(" "),
              })) ?? []),
              ...extraRecipientOptions,
            ]}
          />
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.recipient !== currentValues.recipient
          }
        >
          {({ getFieldValue, setFieldValue }) => {
            const recipient = getFieldValue("recipient");
            if (recipient === undefined) return null;

            const extraOptionsValues = extraRecipientOptions.map((option) => option.value);
            const isExtraOption = extraOptionsValues.includes(recipient);

            setFieldValue("debtorContactId", isExtraOption ? null : recipient);
            setFieldValue("contactType", isExtraOption ? recipient : DEBTOR_CONTACT_VALUE);

            // recipient is either an ID or a value of extra option
            // we split recipient into 2 hidden inputs
            return (
              <>
                <Form.Item name="debtorContactId" hidden>
                  <Input hidden />
                </Form.Item>
                <Form.Item name="contactType" hidden>
                  <Input hidden />
                </Form.Item>
              </>
            );
          }}
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.debtorContactId !== currentValues.debtorContactId ||
            prevValues.contactMethod !== currentValues.contactMethod
          }
        >
          {({ getFieldValue, setFieldValue }) => {
            const contactMethod = getFieldValue("contactMethod");

            if (["phone", "text", "ai_dialer"].includes(contactMethod?.toLowerCase())) {
              const debtorContactId = getFieldValue("debtorContactId");
              const debtorContact = debtorContacts?.find(
                (contact) => contact.id === debtorContactId,
              );
              const options =
                debtorContact?.phones?.map((phone) => ({
                  label: `${phone.number} - ${capitalizeFirstLetter(phone.type)}`,
                  value: phone.number,
                })) ?? [];

              // Preselect if only 1 option is available
              if (options.length === 1) {
                setFieldValue("phoneNumber", options[0].value);
              } else {
                setFieldValue("phoneNumber", undefined);
              }

              return (
                <Form.Item label="Phone Number" name="phoneNumber">
                  <Select popupMatchSelectWidth={false} allowClear options={options} />
                </Form.Item>
              );
            }
          }}
        </Form.Item>
        <Form.Item
          label="Communication Start"
          name="communicationStartTime"
          rules={[{ required: true }]}
        >
          <AktDatePicker
            showSecond={false}
            minuteStep={5}
            showTime
            placeholder="Select Date/Time"
            placement="bottomLeft"
          />
        </Form.Item>
        <Form.Item label="Notes" name="notes">
          <Input.TextArea />
        </Form.Item>
        {CONSENT_FIELDS.map((field) => (
          <Form.Item key={field.name} label={field.label} name={field.name}>
            <Select options={CONSENT_OPTIONS} />
          </Form.Item>
        ))}
        <Form.Item name="isCeaseCommunication" valuePropName="checked">
          <Checkbox>Cease Communication</Checkbox>
        </Form.Item>
      </StyledForm>
    </Modal>
  );
}

export default CommunicationModal;
